import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';

// ---------------------------------------------------------------------------
// WhatsApp per client (opt-in module). Mirrors backend/src/routes/whatsapp.ts.
// ---------------------------------------------------------------------------

export type WhatsAppConnectionState =
  | 'connected'
  | 'pairing'
  | 'logged_out'
  | 'disconnected'
  | 'outdated'
  | 'connecting'
  | 'error';

export interface WhatsAppSyncSummary {
  at: string;
  trigger: 'schedule' | 'manual';
  status: 'ok' | 'not-configured' | 'not-connected' | 'error' | 'busy';
  groups: number;
  synced: number;
  created: number;
  errors: number;
  message?: string;
}

export interface WhatsAppStatus {
  configured: boolean;
  connection: {
    state: WhatsAppConnectionState;
    connected: boolean;
    paired: boolean;
    message: string | null;
    qrExpiresAt: string | null;
    lastError: string | null;
  } | null;
  /** PNG data URL of the pairing QR code, while waiting for the scan. */
  qrDataUrl: string | null;
  error?: string;
  lastSync: WhatsAppSyncSummary | null;
  autoSync: { enabled: boolean; intervalHours: number };
}

export interface WhatsAppGroup {
  id: string;
  chatJid: string;
  name: string;
  clientId: string | null;
  client: { id: string; name: string } | null;
  messageCount: number;
  lastMessageAt: string | null;
  lastSyncedAt: string | null;
  lastError: string | null;
}

export interface WhatsAppMessage {
  id: string;
  senderName: string;
  sentAt: string;
  isFromMe: boolean;
  text: string | null;
  mediaType: string | null;
}

export interface ClientWhatsApp {
  groups: { id: string; name: string; lastMessageAt: string | null; lastSyncedAt: string | null }[];
  groupId: string | null;
  /** Oldest → newest. */
  messages: WhatsAppMessage[];
  hasMore: boolean;
}

interface Envelope<T> {
  success: true;
  data: T;
}

function headers(json = false): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return {
    ...(json ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export async function getWhatsAppStatus(): Promise<WhatsAppStatus> {
  const res = await apiFetch<Envelope<WhatsAppStatus>>('/api/whatsapp/status', {
    headers: headers(),
  });
  return res.data;
}

export async function listWhatsAppGroups(): Promise<WhatsAppGroup[]> {
  const res = await apiFetch<Envelope<WhatsAppGroup[]>>('/api/whatsapp/groups', {
    headers: headers(),
  });
  return res.data;
}

export async function linkWhatsAppGroup(groupId: string, clientId: string | null): Promise<void> {
  await apiFetch<Envelope<unknown>>(`/api/whatsapp/groups/${encodeURIComponent(groupId)}`, {
    method: 'PUT',
    headers: headers(true),
    body: JSON.stringify({ clientId }),
  });
}

/** Ask the WhatsApp service for a new round of QR codes. */
export async function requestWhatsAppQr(): Promise<void> {
  await apiFetch<Envelope<unknown>>('/api/whatsapp/reconnect', {
    method: 'POST',
    headers: headers(),
  });
}

export async function startWhatsAppSync(): Promise<void> {
  await apiFetch<Envelope<unknown>>('/api/whatsapp/sync', { method: 'POST', headers: headers() });
}

export async function getClientWhatsApp(
  clientId: string,
  params: { groupId?: string; before?: string; limit?: number } = {},
): Promise<ClientWhatsApp> {
  const qs = new URLSearchParams();
  if (params.groupId) qs.set('groupId', params.groupId);
  if (params.before) qs.set('before', params.before);
  if (params.limit) qs.set('limit', String(params.limit));
  const suffix = qs.toString() ? `?${qs}` : '';
  const res = await apiFetch<Envelope<ClientWhatsApp>>(
    `/api/clients/${encodeURIComponent(clientId)}/whatsapp${suffix}`,
    { headers: headers() },
  );
  return res.data;
}

/** How a media message reads (the file itself is not synced). */
export function mediaLabel(mediaType: string | null): string | null {
  if (!mediaType) return null;
  const labels: Record<string, string> = {
    image: '📷 Poză',
    video: '🎬 Video',
    audio: '🎤 Mesaj vocal',
    ptt: '🎤 Mesaj vocal',
    document: '📄 Document',
    sticker: 'Sticker',
    gif: 'GIF',
    location: '📍 Locație',
    contact: '👤 Contact',
  };
  return labels[mediaType] ?? mediaType;
}
